// src/pages/PeakHikes.jsx
export default function PeakHikes() {
    return (
      <article className="prose prose-invert max-w-3xl mx-auto px-4 py-12">
        <h1 className="text-3xl font-bold tracking-tight mb-2">
          Oil Crashes, Yields Roll Over: Reading the Setup Into Wednesday's Fed
        </h1>
  
        <p>
          Monday, June 15th. Three signals, one direction. Oil is getting
          crushed, long rates keep printing lower highs, and the 2yr — the
          Fed front runner — just cracked neutral trend for the first time
          since this move started. None of this is a prediction of what the
          Fed does Wednesday. It's a read of what the signal is doing right now.
        </p>
  
        <h2 className="text-2xl font-bold">The Thesis</h2>
        <p>Three things lining up on the same read.</p>
        <ul>
          <li><strong>Oil:</strong> short since June 10, now down another ~5% and confirmed bearish trend. Quad 4 crash, -29% off the April inflation-cycle peak. The equity tape is reading the Strait of Hormuz de-escalation as risk-on. Oil itself doesn't care about the narrative — it's pricing the signal.</li>
          <li><strong>Rates:</strong> both US inflation and bond yields look like they put in a cycle top. Duration added via BNDD and LQD, TLT shorts covered. 2s and 10s both signaling lower highs again this morning.</li>
          <li><strong>VIX:</strong> back below 19 — the investable bucket — with the key trend line at 18.19. Dealer gamma is positive again into a heavy June OPEX week. That's a tailwind for flows, not a reason to chase.</li>
        </ul>
        <p>You don't need all three to be decisive. You need them agreeing on direction. Right now they are.</p>
  
        <h2 className="text-2xl font-bold">Why the Front End Is the Story</h2>
        <p>
          The 2yr yield has been the cleanest tell in this whole setup — it's
          been holding bullish trend for months, but this morning it's showing
          the first real signs of a breakdown toward neutral. If that holds,
          it opens the door to a market conversation about cuts that wasn't on
          the table two weeks ago, especially with a July Quad 4 setup
          (decelerating growth, decelerating inflation) building underneath it.
        </p>
        <p>
          That's the piece worth sitting with going into Wednesday. The Fed
          hasn't said anything yet. The market is already moving like it
          expects something. Whether that's right or wrong gets answered in 48
          hours — the point today is just that the front end is the one to
          watch, not the headline.
        </p>
  
        <h2 className="text-2xl font-bold">Where Capital Is Actually Rotating</h2>
        <ul>
          <li><strong>Out:</strong> oil equities, Norway, commodity baskets, soybeans, corn, wheat — the whole inflation-beneficiary basket that was working a month ago is being unwound.</li>
          <li><strong>Into duration:</strong> BNDD, LQD — the disinflation and IG credit trade, funded by closing out the TLT short.</li>
          <li><strong>Into high growth:</strong> DRAM, QTUM, TINY, BUG, copper/COPX, and XAR remain the strongest bullish-trend, bullish-trade names in the ETF universe. TINY alone was up ~5.7-5.8% and sat as the second-best place to be long in the entire screen.</li>
          <li><strong>Into defense:</strong> health care (PINK, FXH) is being layered in as rate-sensitive, non-cyclical exposure — capital being deployed while staying inside the risk ranges.</li>
        </ul>
        <p>
          Single stocks are telling a different story than the ETFs. Only two
          of the Mag Seven were sitting bullish trend as of this morning — the
          strength in this tape is concentrated in high-growth thematic
          baskets and semis, not broad mega-cap leadership. That's a real
          divergence worth tracking, not noise.
        </p>
  
        <h2 className="text-2xl font-bold">Gold and the Disinflation Print</h2>
        <p>
          Gold is still neutral trend but close to flipping bullish — if
          yields keep breaking down, real yields compress and gold should
          catch a bid. Miners aren't there yet. Separately, the inflation
          nowcast has June's exit rate setting up for a genuine disinflationary
          stepdown, which is the macro backdrop that makes the whole rates
          thesis coherent in the first place.
        </p>
  
        <div className="my-6">
          {/* Drop the relevant chart or Top 3 Things graphic here */}
        </div>
  
        <h2 className="text-2xl font-bold">What This Isn't</h2>
        <p>
          This isn't a call on what the Fed does Wednesday. The signal has
          been unambiguous for two weeks — short oil, add duration, watch the
          front end — but the FOMC statement, the dot plot, and the presser
          are all still ahead of us. Position off what the market is actually
          doing today. Update when new information shows up, not before.
        </p>
        <p>That's the whole post. Three signals, one open question, 48 hours out.</p>
      </article>
    );
  }